import React, { useState } from 'react';
import { X } from 'lucide-react';
import { MemberExtends } from '../../../types/member';
import { formatDate } from '../../../utils/dateUtils';
import { MemberStatus } from './MemberStatus';
import { RoleSelector } from './RoleSelector';

interface MemberDetailsModalProps {
  member: MemberExtends;
  onClose: () => void;
  onCompensate: (amount: number) => void;
}

export function MemberDetailsModal({ member, onClose, onCompensate }: MemberDetailsModalProps) {
  const [showRoleSelector, setShowRoleSelector] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-medium text-gray-900">会員詳細</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500">会員ID</h3>
              <p className="mt-1 text-sm text-gray-900">{member.memberNumber}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">ステータス</h3>
              <div className="mt-1">
                <MemberStatus status={member.status} />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">メールアドレス</h3>
              <p className="mt-1 text-sm text-gray-900">{member.email}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">プラン</h3>
              <p className="mt-1 text-sm text-gray-900">{member.plan}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">登録日</h3>
              <p className="mt-1 text-sm text-gray-900">{formatDate(member.registeredAt)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">最終ログイン</h3>
              <p className="mt-1 text-sm text-gray-900">{member.lastLoginAt ? formatDate(member.lastLoginAt) : '-'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">AIクレジット残高</h3>
              <p className="mt-1 text-sm text-gray-900">
                {member.aiCredits.toLocaleString()} / {member.maxAiCredits.toLocaleString()}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">紹介者コード</h3>
              <p className="mt-1 text-sm text-gray-900">
                {member.referralCode || '-'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">売上金</h3>
              <p className="mt-1 text-sm text-gray-900">
                ¥{member.salesAmount.toLocaleString()}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">ロール</h3>
              <p className="mt-1 text-sm text-gray-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  member.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {member.role === 'admin' ? '管理者' : 'ユーザー'}
                </span>
                <button
                  onClick={() => setShowRoleSelector(!showRoleSelector)}
                  className="ml-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  変更
                </button>
              </p>
            </div>
          </div>
          
          {/* ロールセレクター */}
          {showRoleSelector && (
            <div className="border-t pt-4">
              <RoleSelector 
                userId={member.id} 
                currentRole={member.role || 'user'} 
                onRoleUpdated={() => {
                  setShowRoleSelector(false);
                  onClose();
                }}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}